import { Form, Input, Tag, Button, Row, Col, message } from 'antd'
import { CopyOutlined, SearchOutlined, UsergroupAddOutlined, CloseOutlined, UndoOutlined } from '@ant-design/icons'
import { useMyContext } from 'Context/MyContextProvider'
import React, { useEffect, useState, useRef, useCallback } from 'react'
import { ROW_GUTTER } from 'constants/ThemeConstant'
import ResponsiveModal from 'components/shared-components/ResponsiveModal'
import VirtualList from 'rc-virtual-list'
import { PRIMARY } from '../../CustomUtils.js/utils'
import Flex from 'components/shared-components/Flex'

const chunkArray = (arr, size) => {
    const chunks = [];
    for (let i = 0; i < arr.length; i += size) {
        chunks.push(arr.slice(i, i + size));
    }
    return chunks;
};

const normalizeManualNumbers = (value) => {
    return value
        .replace(/\r/g, '')
        .replace(/[\t ]+/g, '\n') // Replace spaces/tabs with newlines to keep space-separated entries separate
        .replace(/\n+/g, '\n')
        .replace(/\s*,\s*/g, '\n')
        .replace(/[^0-9\n]/g, '');
}

const buildUniqueList = (value) => {
    if (!value) return ''
    const match = value.match(/\n*$/)
    const trailingNewlines = match ? match[0] : ''
    const mainContent = value.slice(0, value.length - trailingNewlines.length)

    const seen = new Set()
    const cleaned = mainContent
        .split('\n')
        .map((item) => item.trim())
        .filter(Boolean)
        .filter((item) => {
            if (seen.has(item)) return false
            seen.add(item)
            return true
        })
        .join('\n')

    return cleaned + trailingNewlines
}

const CommonNumberField = ({ numbers, additionalNumbers = [], number, setNumber, isMobile, onShowGroups }) => {
    const { getNumberCount } = useMyContext()

    const getInitialValue = () => {
        const statusText = (additionalNumbers && additionalNumbers.length > 0) ? additionalNumbers.join('\n') : '';
        const manualText = number ? number : '';
        const combined = [statusText, manualText].filter(Boolean).join('\n');
        return buildUniqueList(combined);
    };

    const [localValue, setLocalValue] = useState(getInitialValue)
    const [modalVisible, setModalVisible] = useState(false)
    const [searchValue, setSearchValue] = useState('')
    const [historyValue, setHistoryValue] = useState('')

    const lastSyncedNumberRef = useRef(number)
    const lastAdditionalNumbersRef = useRef(additionalNumbers)
    const debounceTimeoutRef = useRef(null)

    const filteredNumbers = searchValue
        ? numbers.filter(num => String(num)?.includes(searchValue))
        : numbers

    const columnCount = isMobile ? 3 : 6;
    const chunkedNumbers = chunkArray(filteredNumbers, columnCount);
    const chunkedData = chunkedNumbers.map((rowItems, index) => ({
        id: index,
        items: rowItems
    }));

    const handleCopyAll = () => {
        navigator.clipboard.writeText(numbers.join('\n'))
        message.success('Numbers copied to clipboard!')
    }

    const handleClear = () => {
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }
        if (localValue) {
            setHistoryValue(localValue);
        }
        setLocalValue('');
        lastSyncedNumberRef.current = '';
        setNumber('');
        message.success({ content: 'Cleared manual numbers. Press Ctrl+Z to undo.', key: 'clear-numbers' });
    }

    const handleUndo = useCallback(() => {
        if (historyValue) {
            setLocalValue(historyValue);
            const cleaned = buildUniqueList(normalizeManualNumbers(historyValue));
            lastSyncedNumberRef.current = cleaned;
            setNumber(cleaned);
            setHistoryValue('');
            message.success('Restored manual numbers!');
        }
    }, [historyValue, setNumber]);

    useEffect(() => {
        const isAdditionalChanged = additionalNumbers !== lastAdditionalNumbersRef.current;
        const isNumberChanged = number !== lastSyncedNumberRef.current;

        if (isAdditionalChanged || isNumberChanged) {
            const statusText = (additionalNumbers && additionalNumbers.length > 0) ? additionalNumbers.join('\n') : '';
            const manualText = number ? number : '';
            const combined = [statusText, manualText].filter(Boolean).join('\n');
            const cleaned = buildUniqueList(combined);
            setLocalValue(cleaned);
            lastSyncedNumberRef.current = number;
            lastAdditionalNumbersRef.current = additionalNumbers;
        }
    }, [additionalNumbers, number]);

    useEffect(() => {
        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        const handleKeyDown = (e) => {
            const isUndo = (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z';
            if (isUndo && historyValue && !localValue) {
                e.preventDefault();
                handleUndo();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [historyValue, localValue, handleUndo]);

    return (
        <>
            {numbers?.length > 0 &&
                <Form.Item className='m-0' label="Mobile Number from Groups">
                    {isMobile ? (
                        <Row gutter={[4, 4]} align="middle" wrap={false}>
                            {numbers.slice(0, 3).map((n) => (
                                <Col xs={6} key={n}>
                                    <Tag color="magenta" style={{ width: '100%', textAlign: 'center', margin: 0 }}>{n}</Tag>
                                </Col>
                            ))}
                            {numbers.length > 3 && (
                                <Col xs={6}>
                                    <Button type="link" size="small" style={{ padding: 0, width: '100%' }} onClick={() => setModalVisible(true)}>
                                        View All ({numbers.length})
                                    </Button>
                                </Col>
                            )}
                        </Row>
                    ) : (
                        <div>
                            {numbers.slice(0, 6).map((n) => (
                                <Tag key={n} color={'magenta'}>{n}</Tag>
                            ))}
                            {numbers.length > 6 && (
                                <Button type="link" onClick={() => setModalVisible(true)}>
                                    View All ({numbers.length})
                                </Button>
                            )}
                        </div>
                    )}
                    <ResponsiveModal
                        title={
                            <Row align="middle" justify="space-between" style={{ width: '100%' }}>
                                <Col style={{ fontSize: isMobile ? 15 : 16, fontWeight: 600 }}>
                                    {isMobile ? "Group Numbers" : "Mobile Numbers from Groups"}
                                </Col>
                                <Col>
                                    <Input
                                        placeholder="Search"
                                        prefix={<SearchOutlined />}
                                        value={searchValue}
                                        onChange={(e) => setSearchValue(e.target.value)}
                                        size="small"
                                        style={{ width: isMobile ? 120 : 200 }}
                                    />
                                </Col>
                            </Row>
                        }
                        open={modalVisible}
                        onClose={() => {
                            setModalVisible(false)
                            setSearchValue('')
                        }}
                        modalProps={{
                            width: 860,
                            closable: false,
                            maskClosable: true
                        }}
                        footer={
                            <Row gutter={ROW_GUTTER} justify="space-between" align="middle" style={{ width: '100%', margin: 0 }}>
                                <Col>
                                    <span style={{ color: '#6b7280', fontSize: 13 }}>
                                        Showing: <strong style={{ color: '#1890ff' }}>{filteredNumbers.length}</strong> / {numbers.length}
                                    </span>
                                </Col>
                                <Col style={{ display: 'flex', gap: 8 }}>
                                    <Button key="copy" type="primary" size="small" icon={<CopyOutlined />} onClick={handleCopyAll}>
                                        Copy All
                                    </Button>
                                    <Button key="close" size="small" onClick={() => setModalVisible(false)}>
                                        Close
                                    </Button>
                                </Col>
                            </Row>
                        }
                    >
                        <div style={{ padding: '0 4px' }}>
                            {filteredNumbers.length > 0 ? (
                                isMobile ? (
                                    /* On mobile the Drawer body already scrolls — adding VirtualList
                                       (which needs its own fixed-height scroll container) creates a
                                       second scroll context and causes an infinite top-to-bottom loop.
                                       Plain rendering lets the drawer body handle scroll naturally. */
                                    <Row gutter={[8, 8]}>
                                        {filteredNumbers.map((num, idx) => (
                                            <Col span={24 / columnCount} key={idx}>
                                                <Tag
                                                    onClick={() => {
                                                        navigator.clipboard.writeText(num)
                                                        message.success('Copied!')
                                                    }}
                                                    style={{ cursor: 'pointer', width: '100%', textAlign: 'center', margin: 0 }}
                                                >
                                                    {num}
                                                </Tag>
                                            </Col>
                                        ))}
                                    </Row>
                                ) : (
                                    /* On desktop the Modal has no outer scroll — VirtualList owns its
                                       fixed-height viewport and only renders visible rows. */
                                    <VirtualList
                                        data={chunkedData}
                                        height={400}
                                        itemHeight={34}
                                        itemKey="id"
                                        style={{ overflowX: 'hidden' }}
                                    >
                                        {(row) => (
                                            <Row gutter={[8, 8]} style={{ marginBottom: 8, marginRight: 0, marginLeft: 0 }}>
                                                {row.items.map((num, idx) => (
                                                    <Col span={24 / columnCount} key={idx}>
                                                        <Tag
                                                            onClick={() => {
                                                                navigator.clipboard.writeText(num)
                                                                message.success('Copied!')
                                                            }}
                                                            style={{ cursor: 'pointer', width: '100%', textAlign: 'center', margin: 0 }}
                                                        >
                                                            {num}
                                                        </Tag>
                                                    </Col>
                                                ))}
                                            </Row>
                                        )}
                                    </VirtualList>
                                )
                            ) : (
                                <div style={{ textAlign: 'center', color: '#999', padding: '20px 0' }}>
                                    No numbers found
                                </div>
                            )}
                        </div>
                    </ResponsiveModal>
                </Form.Item>
            }
            <style dangerouslySetInnerHTML={{
                __html: `
                .mobile-numbers-form-item {
                    width: 100%;
                }
                .mobile-numbers-form-item .ant-form-item-label {
                    width: 100% !important;
                    max-width: 100% !important;
                }
                .mobile-numbers-form-item .ant-form-item-label > label {
                    width: 100% !important;
                    display: flex !important;
                    justify-content: space-between !important;
                    align-items: center !important;
                }
                .mobile-numbers-form-item .ant-form-item-label > label::after {
                    display: none !important;
                }
            `}} />
            <Form.Item
                className='m-0 mobile-numbers-form-item'
                label={
                    <Flex justifyContent="space-between" alignItems="center" style={{ width: '100%' }}>
                        <span>{`${numbers?.length > 0 ? 'Additional ' : ''}Mobile Numbers`}</span>
                        <Flex gap={8} alignItems="center">
                            {isMobile && onShowGroups && (
                                <Button
                                    type="dashed"
                                    size="small"
                                    onClick={onShowGroups}
                                    icon={<UsergroupAddOutlined />}
                                    style={{
                                        height: 'auto',
                                        fontSize: 12,
                                        fontWeight: 500,
                                        borderColor: PRIMARY,
                                        color: PRIMARY,
                                    }}
                                >
                                    Select from Groups
                                </Button>
                            )}
                        </Flex>
                    </Flex>
                }
                help={
                    <Flex justifyContent="space-between" alignItems="center" margin="4px 0 0 0">
                        <div>
                            {localValue ? (
                                <Button
                                    type="text"
                                    danger
                                    size="small"
                                    icon={<CloseOutlined />}
                                    onClick={handleClear}
                                >
                                    Clear
                                </Button>
                            ) : (
                                historyValue && (
                                    <Button
                                        type="text"
                                        size="small"
                                        icon={<UndoOutlined />}
                                        onClick={handleUndo}
                                        style={{
                                            fontSize: 12,
                                            padding: 0,
                                            color: '#1890ff',
                                            height: 'auto',
                                        }}
                                    >
                                        Undo Clear
                                    </Button>
                                )
                            )}
                        </div>
                        <span style={{ fontSize: 12, color: '#8c8c8c' }}>
                            Max 10000 Numbers | Total numbers: {getNumberCount(additionalNumbers, number)}
                        </span>
                    </Flex>
                }
            >
                <Input.TextArea
                    rows={5}
                    type="text"
                    value={localValue}
                    onChange={(e) => {
                        const val = e.target.value;
                        // Restrict input to digits, newlines, commas, and spaces only
                        const filteredVal = val.replace(/[^0-9\n\r, ]/g, '');
                        setLocalValue(filteredVal);
                        setHistoryValue(''); // Clear clear-history when typing new text

                        if (debounceTimeoutRef.current) {
                            clearTimeout(debounceTimeoutRef.current);
                        }

                        debounceTimeoutRef.current = setTimeout(() => {
                            const cleaned = buildUniqueList(normalizeManualNumbers(filteredVal));
                            lastSyncedNumberRef.current = cleaned;
                            setNumber(cleaned);
                        }, 500);
                    }}
                    onBlur={(e) => {
                        if (debounceTimeoutRef.current) {
                            clearTimeout(debounceTimeoutRef.current);
                        }
                        const cleaned = buildUniqueList(normalizeManualNumbers(e.target.value));
                        setLocalValue(cleaned);
                        lastSyncedNumberRef.current = cleaned;
                        setNumber(cleaned);
                    }}
                />

            </Form.Item>
        </>
    )
}

export default CommonNumberField
